import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { colorsPrimitives } from '@/shared/design-system/colors';

export type CompanyLogoSize = 'xs' | 'sm' | 'md' | 'ml' | 'lg';

const SIZE_CONFIG: Record<
  CompanyLogoSize,
  { dimension: number; radius: number; fontSize: number }
> = {
  xs: { dimension: 24, radius: 6, fontSize: 11 },
  sm: { dimension: 32, radius: 8, fontSize: 14 },
  md: { dimension: 44, radius: 10, fontSize: 20 },
  ml: { dimension: 56, radius: 12, fontSize: 24 },
  lg: { dimension: 80, radius: 20, fontSize: 36 },
};

export const MONOGRAM_COLORS = [
  colorsPrimitives.darkBlue[800],
  colorsPrimitives.darkBlue[600],
  colorsPrimitives.purple[800],
  colorsPrimitives.emerald[800],
  colorsPrimitives.yellow[900],
  colorsPrimitives.orange[900],
  colorsPrimitives.red[800],
  colorsPrimitives.neutral[400],
];

const REVEAL_DURATION = '240ms';
const REVEAL_BLUR = 'blur(6px)';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const reveal = keyframes`
  from { opacity: 0; filter: ${REVEAL_BLUR}; }
  to { opacity: 1; filter: blur(0); }
`;

const TILE_PROPS = new Set(['$size', '$monogramColor']);

export const Tile = styled('span', {
  shouldForwardProp: (prop) => !TILE_PROPS.has(prop as string),
})<{ $size: CompanyLogoSize; $monogramColor: string | null }>(({
  theme,
  $size,
  $monogramColor,
}) => {
  const { dimension, radius, fontSize } = SIZE_CONFIG[$size];

  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: dimension,
    height: dimension,
    borderRadius: radius,
    overflow: 'hidden',
    backgroundColor: $monogramColor ?? theme.colors.neutral.subtle,
    color: theme.colors.text.primary,
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 600,
    fontSize,
    lineHeight: 1,
    userSelect: 'none',
    transition: `background-color ${REVEAL_DURATION} ease-out`,

    [REDUCED_MOTION]: { transition: 'none' },

    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      boxShadow: `inset 0 0 0 1px ${theme.colors.border.default}`,
      pointerEvents: 'none',
    },
  };
});

export const TileImage = styled('img', {
  shouldForwardProp: (prop) => prop !== '$loaded',
})<{ $loaded: boolean }>(({ $loaded }) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: $loaded ? 1 : 0,
  filter: $loaded ? 'blur(0)' : REVEAL_BLUR,
  transition: `opacity ${REVEAL_DURATION} ease-out, filter ${REVEAL_DURATION} ease-out`,

  [REDUCED_MOTION]: { transition: 'none', filter: 'none' },
}));

export const TileLetter = styled('span')({
  animation: `${reveal} ${REVEAL_DURATION} ease-out`,

  [REDUCED_MOTION]: { animation: 'none' },
});
